// TSA-49: Forecast Comet Icon feature
import { GridChartBaseProps } from "@mantine/charts"
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Indicator,
  SegmentedControl,
  Slider,
  Stack,
  ThemeIcon,
  Tooltip,
} from "@mantine/core"
import { UseFormReturnType } from "@mantine/form"
import { useDisclosure } from "@mantine/hooks"
import {
  IconChartBar,
  IconComet,
  IconHelpCircle,
  IconRulerMeasure2,
  IconTimeline,
} from "@tabler/icons-react"
import _ from "lodash"
import { usePostHog } from "posthog-js/react"
import { useEffect, useMemo, useState } from "react"
import { useSearchParams } from "react-router-dom"

import { emptyEvents, EventsResponses } from "@costory/types/endpoints/events"
import { MetricsResponses } from "@costory/types/endpoints/metrics"
import {
  Filters,
  SearchEvents,
  defaultSearchEvents,
} from "@costory/types/filters"
import { EventCategory, ExplorerChartType } from "@costory/types/prisma-client"

import { DEFAULT_DATE_FORMAT } from "@costory/shared/const"
import dayjs, { Dayjs } from "@costory/shared/dayjs"
import {
  isForecastEnabledDatePreset,
  isGroupByOptional,
} from "@costory/shared/utils/filters"

import { WaterfallSvgComponent } from "@costory/front/components/DatasourceImages"
import { EventsButton } from "@costory/front/components/EventsButton"
import { AggregateBy } from "@costory/front/components/FilterBar"
import { AreaChart } from "@costory/front/components/charts/AreaChart"
import { GetLineChart } from "@costory/front/components/charts/LineChart"
import { GetBarChart } from "@costory/front/components/charts/StackedBarChart"
import { WaterfallChart } from "@costory/front/components/charts/WaterfallChart"
import { QueryWrapper } from "@costory/front/components/layout/QueryWrapper"
import { useChartContext } from "@costory/front/contexts/ChartContext"
import { ModalZoomOnEvents } from "@costory/front/pages/Events/ModalZoomOnEvents"
import { NewEventForm } from "@costory/front/pages/Events/NewEventForm"
import { useEventsQuery } from "@costory/front/queries/explore"
import { disableAllEventSources } from "@costory/front/utils/charts"
import { getBestDrillDown } from "@costory/front/utils/columns"

export type HandlerEvents = {
  eventClickOnChart: (
    val: { agg_date: string },
    setDateRangeFn?: { from: Dayjs; to: Dayjs },
    fromReferenceArea?: boolean,
  ) => void
  createEventFromXAxisClick: (
    event: { value: string },
    saveEvent?: boolean,
  ) => void
  openedSelectedEvents: boolean
  openedNewEvent: boolean
}

type Props = {
  filters: Filters
  height?: number
  withLegend?: boolean
  explorerChartType?: ExplorerChartType
  drillDownInto?: (
    groupBy: string,
    value: string,
    newGroupBy: string,
    mode: "sameWindow" | "newTab",
    advancedExplorerForm?: UseFormReturnType<Filters>,
  ) => void
  setExplorerChartType?: (chartType: ExplorerChartType) => void
  referenceLines?: GridChartBaseProps["referenceLines"]
  setThreshold?: (value: number | null) => void
  threshold?: number | null
  selectedMetric?: MetricsResponses.Metric
  showComparisonChart?: boolean
  setHideComparisonChart?: (val: boolean) => void
  advancedExplorerForm?: UseFormReturnType<Filters>
  filterEventsByChangeDataPointPeriods?: boolean
  showCrossAddEvent?: boolean
  includeForecast?: boolean
  setIncludeForecast?: (value: boolean) => void
  showIntervalBars?: boolean
}

type ForecastButtonProps = {
  includeForecast: boolean
  setIncludeForecast: (value: boolean) => void
  disabled: boolean
}

const ForecastButton = ({
  includeForecast,
  setIncludeForecast,
  disabled,
}: ForecastButtonProps) => {
  const tooltipLabel = disabled
    ? "Forecast (MTD/QTD/YTD or Trailing only)"
    : includeForecast
      ? "Hide Forecast"
      : "Show Forecast"

  return (
    <Tooltip label={tooltipLabel}>
      <Indicator
        inline
        size={16}
        offset={3}
        label="NEW"
        color="primaryHighlight"
        styles={{ indicator: { fontSize: 8, padding: "0 4px" } }}
      >
        <ActionIcon
          onClick={() => setIncludeForecast(!includeForecast)}
          size="xl"
          disabled={disabled}
          variant={includeForecast ? "filled" : "default"}
          color={includeForecast ? "primaryHighlight" : undefined}
          data-intercom-target="explorer-forecast-button"
        >
          <IconComet style={{ width: "70%", height: "70%" }} stroke={1.5} />
        </ActionIcon>
      </Indicator>
    </Tooltip>
  )
}

export const ExplorerMainChart = (props: Props) => {
  const { filterForm, savedView } = useChartContext()
  const [saveEvent, setSaveEvent] = useState<boolean>(false)
  const [fromReferenceArea, setFromReferenceArea] = useState<boolean>(false)
  const [resetEventFilters, setResetEventFilters] = useState<SearchEvents>()
  const posthog = usePostHog()
  // const showInput = <Show form={filterForm} />

  const advancedExplorerBookmarkedEvents =
    props.advancedExplorerForm?.getValues().bookmarkedEvents
  const filterFormBookmarkedEvents = filterForm.getValues().bookmarkedEvents

  const bookmarkedEvents = useMemo(() => {
    return advancedExplorerBookmarkedEvents ?? filterFormBookmarkedEvents ?? []
  }, [advancedExplorerBookmarkedEvents, filterFormBookmarkedEvents])

  const aggregateByInput = (
    <AggregateBy
      form={
        props.advancedExplorerForm ? props.advancedExplorerForm : filterForm
      }
    />
  )
  const currentForm = props.advancedExplorerForm || filterForm
  const eventsFilter = currentForm.values.eventsFilter || defaultSearchEvents

  const eventsQuery = useEventsQuery({
    searchForm: {
      filters: {
        from: props.filters.from,
        to: props.filters.to,
        aggBy: props.filters.aggBy,
      },
      searchEvents: eventsFilter,
      bookmarkedEvents,
    },
  })
  const [openedSelectedEvents, handlersOpenSelectedEvents] =
    useDisclosure(false)

  const [thresholdForViz, setThresholdForViz] = useState(props.threshold || 0)
  const [eventDateRange, setEventDateRange] = useState<
    { from: Dayjs; to: Dayjs } | undefined
  >()

  const [openedNewEvent, handlersNewEventModal] = useDisclosure(false)

  function openEventsModalWithoutDate() {
    setEventDateRange(undefined)
    handlersOpenSelectedEvents.open()
    posthog.capture("Explorer Events Panel Opened", {
      source: "ExplorerMainChart",
    })
  }
  const [showSuggestedEvents, handlersShowSuggestedEvents] = useDisclosure(true)

  function createEventFromXAxisClick(
    event: { value: string },
    saveEvent?: boolean,
  ) {
    const date = dayjs(event.value)
    setSaveEvent(!!saveEvent)
    setEventDateRange({
      from: date.startOf("day"),
      to: date.endOf("day"),
    })
    handlersNewEventModal.open()
    handlersShowSuggestedEvents.close()
  }

  function eventClickOnChart(
    val: { agg_date: string },
    dateRangeDe?: { from: Dayjs; to: Dayjs },
    fromReferenceArea?: boolean,
  ) {
    const startDate = dayjs(val.agg_date)
    const aggregation = props.filters.aggBy.toLowerCase() as
      | "day"
      | "week"
      | "month"

    const dateRange = {
      from: startDate,
      to: startDate.endOf(aggregation),
    }

    if (dateRangeDe) {
      setEventDateRange(dateRangeDe)
    } else {
      setEventDateRange(dateRange)
    }
    setFromReferenceArea(!!fromReferenceArea)
    if (fromReferenceArea) {
      // Reset to default search events configuration first
      setResetEventFilters(
        currentForm.getValues().eventsFilter ?? defaultSearchEvents,
      )
      // Then set the values for reference area (showing all event types)
      currentForm.setFieldValue("eventsFilter", {
        githubSourceRepos: [],
        customBusinessEvents: true,
        customTechnicalEvents: true,
        labels: [],
        commitments: true,
        reservations: true,
        marketplacePurchases: true,
        skuPriceChanges: true,
      })
    }

    handlersOpenSelectedEvents.open()
  }

  const handlerEvents = {
    eventClickOnChart,
    createEventFromXAxisClick,
    openedSelectedEvents,
    openedNewEvent,
  }

  const { currency } = filterForm.getValues()
  const sliderLabelFactory = (value: number) => `${currency} ${value}`
  const [searchParams] = useSearchParams()
  const [showIntervalBars, setShowIntervalBars] = useState(false)

  useEffect(() => {
    setThresholdForViz(props.threshold || 0)
  }, [props.threshold])

  // Reset forecast when date preset changes to an unsupported preset
  const { datePreset } = props.filters
  const { includeForecast, setIncludeForecast } = props
  useEffect(() => {
    const isForecastEnabled = isForecastEnabledDatePreset(datePreset)
    if (!isForecastEnabled && includeForecast && setIncludeForecast) {
      setIncludeForecast(false)
    }
  }, [datePreset, includeForecast, setIncludeForecast])

  // Handle saved views with bookmarked events - disable all event sources on initial load
  useEffect(() => {
    // Only run this effect once on mount when there's a saved view with bookmarked events
    // and no URL parameters (indicating it's a direct saved view access)
    const bookmarkedEventsParam = searchParams.get("bookmarkedEvents")
    const hasBookmarkedEventsInSavedView =
      savedView &&
      (savedView.savedViewEvents.length > 0 ||
        savedView.savedViewExternalEvents.length > 0)

    const hasAnyEventFilter = _.some(savedView?.eventsFilter, (value) => {
      if (Array.isArray(value)) return value.length > 0
      return Boolean(value)
    })

    if (
      hasBookmarkedEventsInSavedView &&
      !bookmarkedEventsParam &&
      !hasAnyEventFilter
    ) {
      disableAllEventSources(currentForm)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {openedNewEvent && (
        <NewEventForm
          isOpen={openedNewEvent}
          onClose={handlersNewEventModal.close}
          defaultDate={(eventDateRange?.from || dayjs()).format(
            DEFAULT_DATE_FORMAT,
          )}
          currentView={props.filters}
          onSaveCallback={(category) => {
            if (saveEvent) {
              disableAllEventSources(currentForm)
            } else {
              const newEventsFilter = {
                ...eventsFilter,
                [category === EventCategory.BUSINESS
                  ? "customBusinessEvents"
                  : "customTechnicalEvents"]: true,
              }
              currentForm.setFieldValue("eventsFilter", newEventsFilter)
            }
          }}
          savedViewId={savedView?.id}
          autoBookmarkEvents={saveEvent}
          advancedExplorerForm={props.advancedExplorerForm}
        />
      )}
      {openedSelectedEvents && (
        <ModalZoomOnEvents
          showSuggestedEvents={
            showSuggestedEvents &&
            props.advancedExplorerForm &&
            props.advancedExplorerForm.values.aggBy == "Day"
          }
          toggleSuggestedEvents={
            props.filterEventsByChangeDataPointPeriods
              ? handlersShowSuggestedEvents.toggle
              : undefined
          }
          filters={_.pick(props.filters, ["from", "to", "aggBy"])}
          isOpened={openedSelectedEvents}
          onClose={() => {
            if (fromReferenceArea) {
              // Reset form to default values when closing from reference area
              const isBookmarkExist =
                currentForm.getValues()?.bookmarkedEvents?.length ?? 0
              if (isBookmarkExist) {
                disableAllEventSources(currentForm)
              } else {
                currentForm.setFieldValue("eventsFilter", resetEventFilters)
              }
            }
            setFromReferenceArea(false)
            handlersOpenSelectedEvents.close()
          }}
          bookmarkedEvents={bookmarkedEvents}
          openNewModal={handlersNewEventModal.open}
          selectedDateRange={eventDateRange}
          currentView={props.filters}
          advancedExplorerForm={props.advancedExplorerForm}
          fromReferenceArea={fromReferenceArea}
        />
      )}
      <Stack h="100%">
        <Flex
          gap={15}
          direction="row"
          align="flex-end"
          style={{ justifyContent: "space-between" }}
        >
          <Group align="flex-end">
            {props.explorerChartType !== ExplorerChartType.WATERFALL &&
              aggregateByInput}
            {props.explorerChartType !== ExplorerChartType.WATERFALL && (
              <EventsButton
                onOpen={openEventsModalWithoutDate}
                eventsQuery={eventsQuery}
              />
            )}
            {props.setIncludeForecast &&
              props.explorerChartType !== ExplorerChartType.WATERFALL && (
                <ForecastButton
                  includeForecast={props.includeForecast || false}
                  setIncludeForecast={props.setIncludeForecast}
                  disabled={
                    !isForecastEnabledDatePreset(props.filters.datePreset)
                  }
                />
              )}
            {props.includeForecast &&
              props.explorerChartType !== ExplorerChartType.WATERFALL && (
                <Tooltip label="Interval bars">
                  <ActionIcon
                    size="xl"
                    variant={showIntervalBars ? "light" : "subtle"}
                    onClick={() => setShowIntervalBars((v) => !v)}
                    data-intercom-target="explorer-interval-bars-toggle"
                  >
                    <IconRulerMeasure2
                      style={{ width: "70%", height: "70%" }}
                      stroke={1.5}
                    />
                  </ActionIcon>
                </Tooltip>
              )}
          </Group>
          <Group align="center" style={{ "--group-justify": "flex-end" }}>
            {props.explorerChartType === ExplorerChartType.WATERFALL &&
              props.setThreshold && (
                <Flex align="center" gap={15}>
                  <Tooltip
                    w={400}
                    multiline={true}
                    label="Combine all items with an absolute value below this threshold."
                  >
                    <ThemeIcon
                      style={{ cursor: "pointer" }}
                      c="primaryHighlight"
                      size="sm"
                    >
                      <IconHelpCircle />
                    </ThemeIcon>
                  </Tooltip>
                  <Box w={200}>
                    <Slider
                      max={1000}
                      step={250}
                      restrictToMarks
                      labelAlwaysOn
                      label={(value) => sliderLabelFactory(value)}
                      value={thresholdForViz}
                      onChange={(value) => {
                        setThresholdForViz(value)
                      }}
                      onChangeEnd={(value) => {
                        props.setThreshold!(value)
                        posthog.capture(
                          "Explorer Waterfall Threshold Adjusted",
                          {
                            threshold: value,
                            source: "ExplorerMainChart",
                          },
                        )
                      }}
                      data-intercom-target="costory-tour-explorer-waterfall-threshold"
                    />
                  </Box>
                </Flex>
              )}
            {props.explorerChartType && (
              <SegmentedControl
                data={[
                  {
                    value: ExplorerChartType.BAR,
                    label: (
                      <Tooltip label="Bar">
                        <ActionIcon
                          size="sm"
                          style={{ pointerEvents: "none" }}
                          aria-label="Bar"
                          data-intercom-target="costory-tour-chart-type-bar"
                        >
                          <IconChartBar size="inherit" />
                        </ActionIcon>
                      </Tooltip>
                    ),
                  },
                  {
                    value: ExplorerChartType.LINE,
                    label: (
                      <Tooltip label="Line">
                        <ActionIcon
                          size="sm"
                          style={{ pointerEvents: "none" }}
                          aria-label="Line"
                          data-intercom-target="costory-tour-chart-type-line"
                        >
                          <IconTimeline size="inherit" />
                        </ActionIcon>
                      </Tooltip>
                    ),
                  },
                  {
                    value: ExplorerChartType.WATERFALL,
                    label: (
                      <Tooltip label="Waterfall">
                        <ActionIcon
                          size="sm"
                          style={{ pointerEvents: "none" }}
                          aria-label="Waterfall"
                          data-intercom-target="costory-tour-chart-type-waterfall"
                        >
                          <WaterfallSvgComponent />
                        </ActionIcon>
                      </Tooltip>
                    ),
                  },
                ]}
                value={props.explorerChartType}
                onChange={(value) => {
                  return (
                    value &&
                    props.setExplorerChartType?.(value as ExplorerChartType)
                  )
                }}
              />
            )}
          </Group>
        </Flex>
        <Flex direction="column" style={{ flex: 1, minHeight: 0 }}>
          <QueryWrapper
            query={eventsQuery}
            allowEmptyArray
            defaultData={emptyEvents}
          >
            {({ data: events }) => {
              return (
                <Graph
                  {...props}
                  filterEventsByChangeDataPointPeriods={
                    props.filterEventsByChangeDataPointPeriods &&
                    showSuggestedEvents
                  }
                  referenceLines={props.referenceLines}
                  handlerEvents={handlerEvents}
                  events={events}
                  includeForecast={props.includeForecast}
                  showIntervalBars={showIntervalBars}
                />
              )
            }}
          </QueryWrapper>
        </Flex>
      </Stack>
    </>
  )
}

function Graph({
  filters,
  height,
  explorerChartType,
  withLegend = true,
  drillDownInto = () => {},
  referenceLines,
  threshold,
  handlerEvents,
  events,
  selectedMetric,
  showComparisonChart,
  setHideComparisonChart,
  advancedExplorerForm,
  filterEventsByChangeDataPointPeriods = false,
  showCrossAddEvent = false,
  includeForecast,
  showIntervalBars = false,
}: Props & {
  handlerEvents: HandlerEvents
  events: EventsResponses.Aggregated
}) {
  const posthog = usePostHog()
  const withMetricsLine = !!selectedMetric && selectedMetric.technical === false
  const showComparisonWithLegend =
    isGroupByOptional(filters.groupBy) && showComparisonChart
      ? filters.metricId === "contracted_cost"
        ? "Contracted Cost"
        : "Cost"
      : undefined
  switch (explorerChartType) {
    case ExplorerChartType.LINE: {
      return (
        <Flex direction="column" h="100%" style={{ minHeight: 0 }}>
          <GetLineChart
            height={height}
            filters={filters}
            withLegend={withLegend}
            withMetricsLine={withMetricsLine}
            events={events}
            handlerEvents={handlerEvents}
            showEventPointer
            showComparisonWithLegend={showComparisonWithLegend}
            setHideComparisonChart={setHideComparisonChart}
            showTrends={!!advancedExplorerForm}
            filterEventsByChangeDataPointPeriods={
              filterEventsByChangeDataPointPeriods
            }
            showCrossAddEvent={showCrossAddEvent}
            includeForecast={includeForecast}
            showIntervalBars={showIntervalBars}
          />
        </Flex>
      )
    }
    case ExplorerChartType.WATERFALL: {
      return (
        <WaterfallChart
          height={height}
          filters={{ ...filters, threshold }}
          withLegend={withLegend}
          dashboardChartConfig={75}
          drillDownInto={(value, mode) => {
            posthog.capture("Waterfall Drill Down", {
              dimensionValue: value,
              mode,
              source: "WaterfallChart",
              groupBy: filters.groupBy,
              context: "waterfall",
            })
            drillDownInto(
              filters.groupBy,
              value,
              getBestDrillDown(filters.groupBy),
              mode,
              advancedExplorerForm,
            )
          }}
        />
      )
    }
    case ExplorerChartType.AREA: {
      return (
        <AreaChart height={height} filters={filters} withLegend={withLegend} />
      )
    }
    default: {
      return (
        <Flex direction="column" h="100%" style={{ minHeight: 0 }}>
          <GetBarChart
            handlerEvents={handlerEvents}
            height={height}
            filters={filters}
            withLegend={withLegend}
            events={events}
            drillDownInto={(value, mode) => {
              posthog.capture("Explorer - Bar Chart Drill Down", {
                dimensionValue: value,
                mode,
                source: "TableChart",
                groupBy: filters.groupBy,
                context: "Explorer",
              })
              drillDownInto(
                filters.groupBy,
                value,
                getBestDrillDown(filters.groupBy),
                mode,
                advancedExplorerForm,
              )
            }}
            referenceLines={referenceLines}
            withMetricsLines={withMetricsLine}
            showEventPointer
            showComparisonWithLegend={showComparisonWithLegend}
            setHideComparisonChart={setHideComparisonChart}
            showTrends={!!advancedExplorerForm}
            filterEventsByChangeDataPointPeriods={
              filterEventsByChangeDataPointPeriods
            }
            showCrossAddEvent={showCrossAddEvent}
            includeForecast={includeForecast}
            showIntervalBars={showIntervalBars}
          />
        </Flex>
      )
    }
  }
}
